import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useApp } from "../context/AppContext";
import { Spinner } from "../components/ui";
import { PLANS } from "../lib/plans";
import { CheckCircle2, AlertTriangle, Clock, ArrowRight } from "lucide-react";

const L = (lang, fr, en) => (lang === "fr" ? fr : en);
const param = (name) => {
  const m = new RegExp("[?&]" + name + "=([^&#]*)").exec(window.location.search);
  return m ? decodeURIComponent(m[1]) : "";
};

export default function BillingSuccess() {
  const { user, authLoading, lang } = useApp();
  const navigate = useNavigate();
  const [state, setState] = useState("checking"); // checking | paid | pending | failed | error
  const [planId, setPlanId] = useState("");
  const tries = useRef(0);

  useEffect(() => {
    if (authLoading) return undefined;
    if (!user) { navigate("/login"); return undefined; }
    const sid = param("session_id");
    if (!sid) { setState("error"); return undefined; }
    let stopped = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/billing/status/${sid}`);
        if (data.plan_id) setPlanId(data.plan_id);
        if (data.payment_status === "paid") { setState("paid"); return; }
        if (data.status === "expired") { setState("failed"); return; }
      } catch { /* keep retrying */ }
      tries.current += 1;
      if (tries.current >= 6) { setState("pending"); return; }
      if (!stopped) setTimeout(poll, 2000);
    };
    poll();
    return () => { stopped = true; };
  }, [user, authLoading, navigate]);

  const planName = PLANS.find((p) => p.id === planId)?.name[lang] || planId;

  const views = {
    checking: { icon: <Spinner className="w-8 h-8 text-text-secondary" />, title: L(lang, "Vérification du paiement…", "Verifying your payment…"), body: L(lang, "Un instant, nous confirmons votre abonnement.", "One moment, we're confirming your subscription.") },
    paid: { icon: <CheckCircle2 className="w-12 h-12 text-emerald-500" />, title: L(lang, "Paiement confirmé", "Payment confirmed"), body: L(lang, `Votre plan ${planName} est actif. Profitez de toutes les fonctionnalités.`, `Your ${planName} plan is active. Enjoy all the features.`) },
    pending: { icon: <Clock className="w-12 h-12 text-amber-500" />, title: L(lang, "Paiement en cours de traitement", "Payment processing"), body: L(lang, "Votre paiement est en cours. Votre plan s'activera dès la confirmation.", "Your payment is processing. Your plan will activate once confirmed.") },
    failed: { icon: <AlertTriangle className="w-12 h-12 text-red-500" />, title: L(lang, "Paiement non finalisé", "Payment not completed"), body: L(lang, "Le paiement a été annulé ou a expiré. Vous pouvez réessayer.", "The payment was cancelled or expired. You can try again.") },
    error: { icon: <AlertTriangle className="w-12 h-12 text-red-500" />, title: L(lang, "Session introuvable", "Session not found"), body: L(lang, "Nous n'avons pas trouvé cette session de paiement.", "We couldn't find this checkout session.") },
  };
  const v = views[state];

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-5" data-testid="billing-success-page">
      <div className="max-w-md w-full text-center rounded-2xl border border-border bg-surface p-10 shadow-xl">
        <div className="flex justify-center mb-5" data-testid={`billing-success-state-${state}`}>{v.icon}</div>
        <h1 className="font-heading font-bold text-2xl text-text-primary mb-2">{v.title}</h1>
        <p className="text-text-secondary mb-7">{v.body}</p>
        <div className="flex items-center justify-center gap-3">
          <button onClick={() => navigate("/")} data-testid="billing-success-app"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-primary text-white font-semibold hover:opacity-90 transition-opacity">
            {L(lang, "Ouvrir l'app", "Open the app")} <ArrowRight className="w-4 h-4" />
          </button>
          {(state === "failed" || state === "error") && (
            <button onClick={() => navigate("/pricing")} data-testid="billing-success-retry"
              className="px-5 py-2.5 rounded-lg border border-border text-text-primary font-semibold hover:bg-surface-hover transition-colors">
              {L(lang, "Voir les tarifs", "See pricing")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
